// Templates — practical artifacts (briefs, scorecards, checklists) rendered as
// HowTo-schema pages. They convert search demand for "<thing> template" into
// in-product trials and capture AEO traction by being the canonical source.

export interface TemplateArticle {
  slug: string;
  primaryKeyword: string;
  searchVolume?: number;
  title: string;
  seoTitle: string;
  metaDescription: string;
  intro: string;
  updated: string;
  readingTime: string;
  /** HowTo steps — rendered as schema.org/HowTo and as numbered list on page. */
  steps: { name: string; text: string }[];
  /** Markdown body — appears after intro, before steps. */
  body: string;
  /** Markdown for the fully-worked example shown after the steps. */
  example: string;
  faq: { q: string; a: string }[];
  related?: { comparisons?: string[]; glossary?: string[]; playbooks?: string[] };
}

export const TEMPLATES_PATH = "/templates";

export const templates: TemplateArticle[] = [
  {
    slug: "campaign-brief-template",
    primaryKeyword: "campaign brief template",
    searchVolume: 320,
    updated: "2026-06-20",
    readingTime: "7 min read",
    title: "Campaign brief template (with worked example)",
    seoTitle: "Campaign Brief Template + Worked Example (2026)",
    metaDescription:
      "A free campaign brief template used by lean B2B marketing teams. Includes the 9 sections, a worked example, and the schema we use inside STRAETCH.",
    intro:
      "Most campaigns fail at the brief, not at execution. A good campaign brief makes the goal, audience, message and definition of done legible to everyone — including the AI tools you use to draft assets. This template gives you the nine sections lean B2B teams actually need.",
    steps: [
      {
        name: "Name the campaign and tie it to a strategic objective",
        text: "Write one sentence that links the campaign to the company OKR or strategic theme it serves. If you can't, the campaign is not ready to brief.",
      },
      {
        name: "Define the primary audience and the job-to-be-done",
        text: "Pick one primary ICP and one job-to-be-done. Resist the urge to list three. Specificity is what makes the AI draft useful.",
      },
      {
        name: "Write the single message",
        text: "One sentence the audience should be able to repeat after the campaign. Everything else supports it.",
      },
      {
        name: "Set the success metric and target",
        text: "One leading metric (e.g. qualified pipeline) with a number and a date. Add at most one supporting metric.",
      },
      {
        name: "Choose channels and formats",
        text: "Map the message to channels where the audience actually pays attention. Two channels run well beats five run badly.",
      },
      {
        name: "List the assets and owners",
        text: "Every asset gets one owner and one due date. Reviewers are named, not implied.",
      },
      {
        name: "Capture the brand voice and no-go rules",
        text: "Even a one-line brand voice note (e.g. 'plainspoken, no hype') anchors AI drafts.",
      },
      {
        name: "Define 'done'",
        text: "What does shipped mean — live, attributed, measured? Write it down so nobody renegotiates at the end.",
      },
      {
        name: "Schedule the review",
        text: "Book the post-campaign review now, with the right metrics shown. Otherwise the loop doesn't close.",
      },
    ],
    body: `**TL;DR** — A campaign brief is the contract between strategy and execution. The nine-section template below is the one we use inside STRAETCH and have refined with lean B2B teams across SaaS, fintech and consumer electronics. It is also AI-ready: each section corresponds to a field a [contextual AI](/glossary/contextual-ai) system needs to draft assets in your voice.

## Why most campaign briefs underperform

In our work with 200+ lean B2B teams, weak briefs share three patterns:

- **Activity, not outcome.** "Run a webinar" is not a campaign goal.
- **Three audiences.** Trying to talk to CMOs, RevOps and SDRs in the same campaign produces messaging nobody acts on.
- **No definition of done.** The campaign quietly trails off; nobody runs a review.

The template below is structured to make each of those mistakes hard.

## The nine sections of a working brief

Each section maps to a [step below](#how-to-use-the-template). Treat them as required, not optional — the discipline is the value.`,
    example: `**Campaign name** — Q3 2026: From audit to action

**Strategic objective** — Convert dormant trial users into paying teams.

**Primary audience** — Heads of Marketing at 10–50-person B2B SaaS companies who completed onboarding but did not invite a teammate within 30 days.

**Job-to-be-done** — "Show me that this tool will save my team's time this quarter, not next year."

**Single message** — Your strategy is already in STRAETCH. Inviting your team turns it into pipeline.

**Success metric** — 60 trial-to-paid conversions by 30 September.

**Channels** — Lifecycle email (3-touch sequence) + one LinkedIn ad set retargeting trial visitors.

**Assets** — Email 1 (D. Hardarson, 5 Aug), Email 2 (D. Hardarson, 12 Aug), Email 3 (D. Hardarson, 19 Aug), LinkedIn creative (Design, 1 Aug), Landing page refresh (Design, 1 Aug).

**Brand voice** — Plainspoken, founder-led, no hype. No emojis. No "supercharge".

**Done** — All assets live, attribution wired to /signup, post-campaign review booked for 7 October with conversion-to-paid as the headline metric.`,
    faq: [
      {
        q: "What should a campaign brief include?",
        a: "Nine sections: campaign name and strategic objective, primary audience and job-to-be-done, single message, success metric and target, channels and formats, assets and owners, brand voice and no-go rules, definition of done, and a scheduled post-campaign review.",
      },
      {
        q: "How long should a campaign brief be?",
        a: "One page is the target. If the brief runs longer than a page, it usually contains either two campaigns or unresolved disagreements that need a conversation, not more bullet points.",
      },
      {
        q: "Can AI write a campaign brief?",
        a: "Yes — with a caveat. Contextual AI tools that know your strategy, brand and audience can draft a credible first version of every section. The marketer's job becomes editing for nuance and locking the definition of done, not authoring from a blank page.",
      },
      {
        q: "Do small teams really need a formal brief?",
        a: "Yes. The cost of a one-page brief is 30 minutes. The cost of a no-brief campaign is a half-quarter of misaligned execution. The smaller the team, the higher the cost of misalignment per person.",
      },
    ],
    related: {
      comparisons: ["hubspot-alternatives", "marketing-project-management-software"],
      glossary: ["campaign-brief", "contextual-ai", "ideal-customer-profile"],
      playbooks: ["contextual-ai-marketing"],
    },
  },
  {
    slug: "marketing-roi-template",
    primaryKeyword: "marketing roi template",
    searchVolume: 480,
    updated: "2026-06-20",
    readingTime: "8 min read",
    title: "Marketing ROI template (with worked example)",
    seoTitle: "Marketing ROI Template + Formula & Example (2026)",
    metaDescription:
      "A free marketing ROI template for lean B2B teams. Includes the formula, the seven inputs you actually need, a worked example, and the dashboard layout we use inside STRAETCH.",
    intro:
      "Most marketing ROI calculations either flatter the team or punish it — rarely do they help anyone decide what to do next. This template gives you a defensible formula, the seven inputs that actually matter, and a one-page dashboard structure built for lean B2B teams reporting up to a CEO or board.",
    steps: [
      {
        name: "Pick the reporting period and lock it",
        text: "Choose a period long enough for your sales cycle to close (typically a quarter for B2B SaaS). Locking it prevents the common trap of re-cutting numbers until the answer looks good.",
      },
      {
        name: "Capture total marketing investment",
        text: "Sum all marketing-attributed cost in the period: people (loaded cost), tools, paid media, agencies, content production, events. If you would not spend it without a marketing function, it counts.",
      },
      {
        name: "Capture marketing-sourced pipeline",
        text: "Sum the value of opportunities created in the period where the first-touch or last-touch source is a marketing channel. Pick one attribution model and use it consistently — switching mid-year breaks the trend line.",
      },
      {
        name: "Capture marketing-influenced closed-won revenue",
        text: "Sum the closed-won revenue from opportunities where marketing had at least one tracked touch. This is the numerator most CEOs actually care about.",
      },
      {
        name: "Calculate ROI using the agreed formula",
        text: "Marketing ROI = (Marketing-attributable gross profit − Marketing investment) ÷ Marketing investment. Express as a multiple (e.g. 3.2x) and a percentage. Note which attribution model you used.",
      },
      {
        name: "Calculate CAC and CAC payback period",
        text: "CAC = Marketing + Sales investment ÷ New customers acquired. CAC payback = CAC ÷ (ARR per customer × gross margin %). Report both alongside ROI — ROI in isolation hides cash-flow risk.",
      },
      {
        name: "Write the one-paragraph narrative",
        text: "Explain what changed versus the previous period and why, in plain language. This is the part executives actually read. Without it, the dashboard is decoration.",
      },
    ],
    body: `**TL;DR** — Marketing ROI is one of the most-debated numbers in B2B. The reason is rarely the math; it is that teams measure different things, change the definition mid-year, and report activity (MQLs, sessions) instead of outcome (pipeline, revenue). This template fixes the definition once so you can spend the rest of the year arguing about what to do, not how to count.

## The formula

\`\`\`
Marketing ROI = (Marketing-attributable gross profit − Marketing investment) ÷ Marketing investment
\`\`\`

Reported as a multiple (3.2x) and a percentage (220%). For SaaS, gross profit ≈ revenue × gross margin (typically 70–85%).

A useful ROI report includes three numbers, not one:

- **Marketing ROI** — the headline.
- **CAC** — the cost of acquiring one new customer (marketing + sales).
- **CAC payback period** — months to recover CAC from gross profit per customer.

ROI without CAC payback hides the cash-flow risk of scaling. Lean B2B teams should target CAC payback under 18 months and trending down.

## The seven inputs

1. Reporting period (quarter recommended for B2B SaaS).
2. Total marketing investment (people, tools, media, agencies, content, events).
3. Marketing-sourced pipeline value.
4. Marketing-influenced closed-won revenue.
5. Gross margin % (from finance).
6. New customers acquired.
7. ARR per customer (or average contract value).

Everything else on most dashboards is a vanity metric.

## Attribution: pick one, write it down

The arguments about marketing ROI are almost always arguments about attribution. The fix is not to find the "right" model — none of them are right. The fix is to pick one, document it on the dashboard, and use it for at least a year.

- **First-touch** — credits the channel that introduced the buyer. Best for awareness-heavy strategies.
- **Last-touch** — credits the channel that closed the loop. Best for performance-heavy strategies.
- **Linear / time-decay** — splits credit across touches. Best for considered B2B purchases.

For most lean B2B teams, **first-touch for top-of-funnel reporting** and **last-touch for ROI reporting** is a defensible split. Whatever you choose, put the choice on the dashboard so nobody re-litigates it monthly.

## The one-page dashboard

The dashboard a CEO will actually use has six blocks on one screen:

- ROI multiple and trend (last 4 periods).
- CAC and CAC payback trend.
- Pipeline sourced by channel.
- Closed-won by channel.
- Top three campaigns by ROI.
- A 3–5 sentence written narrative explaining the period.

The narrative is the part most teams skip and most executives read first. Inside [STRAETCH](/ai-capabilities), the contextual AI drafts it from the underlying numbers; the marketer edits for nuance.`,
    example: `**Period** — Q2 2026 (Apr–Jun)
**Attribution model** — Last-touch for ROI; first-touch for top-of-funnel.

**Inputs**
- Marketing investment: €182,000 (2 FTE loaded €120k, tools €18k, paid media €28k, content €16k)
- Marketing-sourced pipeline: €1,240,000
- Marketing-influenced closed-won: €486,000
- Gross margin: 78%
- New customers acquired: 14
- ARR per customer: €34,700
- Sales investment in period: €148,000

**Outputs**
- Marketing-attributable gross profit = €486,000 × 78% = €379,080
- Marketing ROI = (€379,080 − €182,000) ÷ €182,000 = **1.08x (108%)**
- CAC = (€182,000 + €148,000) ÷ 14 = **€23,571**
- CAC payback = €23,571 ÷ (€34,700 × 78% ÷ 12) = **10.4 months**

**Narrative** — Q2 ROI came in at 1.08x, down from 1.4x in Q1, driven by a €22k step-up in paid media that did not yet convert (long sales cycle in the enterprise segment). CAC payback improved to 10.4 months as average contract value rose 9% QoQ. Recommendation: hold media spend flat in Q3, double down on the two top-ROI campaigns (Contextual AI playbook launch and the HubSpot-alternatives content cluster), and revisit the enterprise media test in Q4 once Q2 cohorts mature.`,
    faq: [
      {
        q: "What is a good marketing ROI for B2B SaaS?",
        a: "For mature B2B SaaS, 3:1 to 5:1 (3x–5x) is a healthy range. Early-stage companies investing in category creation or brand will run lower, often 1:1 to 2:1, with CAC payback used as the secondary health check. Anything reported above 10:1 usually has an attribution problem, not a marketing miracle.",
      },
      {
        q: "What is the formula for marketing ROI?",
        a: "Marketing ROI = (Marketing-attributable gross profit − Marketing investment) ÷ Marketing investment. Express as a multiple and a percentage. Use gross profit (not revenue) so the number is comparable across companies with different gross margins.",
      },
      {
        q: "How is marketing ROI different from ROAS?",
        a: "ROAS (Return on Ad Spend) measures revenue from a single channel divided by spend on that channel. Marketing ROI measures gross profit from all marketing-attributable revenue divided by all marketing investment, including people and tools. ROAS is a channel optimisation metric; ROI is a business-level metric.",
      },
      {
        q: "Should marketing ROI include salaries?",
        a: "Yes. Excluding people cost makes ROI look flattering and makes year-over-year comparisons meaningless when team size changes. Use loaded cost (salary + employer taxes + benefits + allocated overhead).",
      },
      {
        q: "How often should marketing ROI be reported?",
        a: "Quarterly for B2B with sales cycles over 30 days. Monthly reporting creates noise from in-flight pipeline that hasn't closed. The exception is paid-media-heavy DTC or PLG motions, where weekly cohort ROI is informative.",
      },
    ],
    related: {
      comparisons: ["hubspot-alternatives", "marketing-project-management-software"],
      glossary: ["marketing-roi-dashboard", "single-source-of-truth", "sales-and-marketing-alignment"],
      playbooks: ["contextual-ai-marketing"],
    },
  },
];

export function getTemplateBySlug(slug: string): TemplateArticle | undefined {
  return templates.find((t) => t.slug === slug);
}
